/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import RefreshIcon from '@mui/icons-material/Refresh';
import WarningAmber from '@mui/icons-material/WarningAmber';
import {
  alpha,
  Box,
  IconButton,
  Link as MuiLink,
  Stack,
  type Theme,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  type BidRequestInputApprovalStatus,
  type BidRequestsQueryListColumns,
  type ObjectIdMap,
  SalesforceObject,
} from '@poweralpha/common';
import dayjs from 'dayjs';
import { useState } from 'react';
import { usePermissions } from '../../auth';
import { useBidRequestsQuery } from '../../bid-requests/api';
import { ApprovalIcon } from '../../bid-requests/components/ApprovalIcon';
import { determineBidRequestLandingPage } from '../../bid-requests/utils';
import { useSfIdMapQuery } from '../../project/api';
import { Emoji } from '../../react-components/Emoji';
import RecordList from '../../react-components/RecordList';
import Widget from '../../react-components/Widget';

const staleDate = dayjs().subtract(30, 'days');
const BR_PAGE_SIZE = 20;

const SmallApprovalIcon = ({
  approval,
  label,
  superAdminApproval = false,
}: {
  approval: BidRequestInputApprovalStatus;
  label: string;
  superAdminApproval?: boolean;
}) => (
  <Box sx={{ display: 'inline-flex', '& svg': { fontSize: 14 } }}>
    <ApprovalIcon approval={approval} label={label} superAdminApproval={superAdminApproval} />
  </Box>
);

const getBidRequestItemSx = (br: BidRequestsQueryListColumns) => (theme: Theme) => {
  const staleColor = alpha(theme.palette.warning.main, theme.palette.mode === 'dark' ? 0.28 : 0.2);
  const staleHoverColor = alpha(
    theme.palette.warning.main,
    theme.palette.mode === 'dark' ? 0.4 : 0.3,
  );
  const isStale = dayjs(br.modified_at).isBefore(staleDate);

  return {
    px: 0,
    backgroundColor: isStale ? staleColor : undefined,
    '&:hover': {
      backgroundColor: isStale ? staleHoverColor : alpha(theme.palette.primary.main, 0.08),
    },
  };
};

const getRowCallback =
  (projectIdMap: ObjectIdMap | undefined, opportunityIdMap: ObjectIdMap | undefined) =>
  (row: BidRequestsQueryListColumns) => {
    const opportunityName = opportunityIdMap?.[row.opportunity_id] ?? row.opportunity_id;
    const projectNames = row.project_ids.map((id) => projectIdMap?.[id] ?? id).join(', ');

    return (
      <Stack
        spacing={0.45}
        sx={{
          width: '100%',
          px: 1.5,
        }}
      >
        <Stack
          direction="row"
          sx={{
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 1,
          }}
        >
          <MuiLink
            component="span"
            underline="always"
            sx={{ fontSize: 13, fontWeight: 600, lineHeight: 1.2 }}
            noWrap
          >
            {row.name}
          </MuiLink>
          <Stack direction="row" spacing={0.25}>
            <SmallApprovalIcon
              approval={row.dev_approval}
              label="Dev Approval"
              superAdminApproval={Boolean(row.dev_approval_meta?.super_admin_approval)}
            />
            <SmallApprovalIcon
              approval={row.txm_approval}
              label="TXM Approval"
              superAdminApproval={Boolean(row.txm_approval_meta?.super_admin_approval)}
            />
            <SmallApprovalIcon approval={row.eng_approval} label="Eng Approval" />
            <SmallApprovalIcon approval={row.finance_approval} label="Finance Approval" />
          </Stack>
        </Stack>
        <Stack>
          <Tooltip title={`Opportunity: ${opportunityName}`}>
            <Typography
              variant="caption"
              noWrap
              sx={{
                color: 'text.secondary',
                lineHeight: 1.1,
              }}
            >
              Opportunity: {opportunityName}
            </Typography>
          </Tooltip>
          <Tooltip title={`Projects: ${projectNames || '-'}`}>
            <Typography
              variant="caption"
              noWrap
              sx={{
                color: 'text.secondary',
                lineHeight: 1.1,
              }}
            >
              Projects: {projectNames || '-'}
            </Typography>
          </Tooltip>
        </Stack>
      </Stack>
    );
  };

function ActionableBidRequests() {
  const permissions = usePermissions();
  const [limit, setLimit] = useState(BR_PAGE_SIZE);
  const brQuery = useBidRequestsQuery({
    onlyMe: true,
    completionStatus: 'open',
    latestVersionOnly: true,
    limit,
    offset: 0,
  });

  const { data: opportunityIdMap } = useSfIdMapQuery(
    SalesforceObject.opportunity,
    brQuery.data?.rows.map((row) => row.opportunity_id) ?? [],
  );
  const { data: projectIdMap } = useSfIdMapQuery(
    SalesforceObject.project,
    brQuery.data?.rows.flatMap((row) => row.project_ids) ?? [],
  );

  return (
    <Widget
      title="Actionable Bid Requests"
      titleContent={
        brQuery.data?.count ? (
          <Stack
            direction="row"
            spacing={0}
            sx={{
              alignItems: 'center',
            }}
          >
            <WarningAmber color="warning" sx={{ height: 18, width: 18 }} />
            <Typography
              component="span"
              sx={{ color: 'warning.main', fontSize: 12, fontWeight: 'fontWeightBold' }}
            >
              {brQuery.data?.count}
            </Typography>
          </Stack>
        ) : (
          <Emoji symbol="✅" />
        )
      }
      actions={
        <IconButton loading={brQuery.isRefetching} size="small" onClick={() => brQuery.refetch()}>
          <RefreshIcon fontSize="small" />
        </IconButton>
      }
      redirectPath="/bid-requests?onlyMine=mine&cs=true"
    >
      <RecordList
        label="bid request"
        queryResult={brQuery}
        getRow={getRowCallback(projectIdMap, opportunityIdMap)}
        getLink={(row) =>
          `/bid-requests/${row.uuid}/${determineBidRequestLandingPage(permissions)}/`
        }
        idKey="id"
        listItemHeight={62}
        customButtonSx={getBidRequestItemSx}
        onLoadMore={() => setLimit((current) => current + BR_PAGE_SIZE)}
        isLoadingMore={brQuery.isFetching && !brQuery.isLoading}
      />
    </Widget>
  );
}

export default ActionableBidRequests;
